if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.initAttendeeFields();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.form.checkValidity()) {
          this.form.reportValidity();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      initAttendeeFields() {
        this.attendeeFields = this.form.querySelector('[data-attendee-fields]');
        if (!this.attendeeFields) return;

        this.attendeeExtraFields = this.attendeeFields.querySelector('[data-attendee-extra-fields]');
        if (!this.attendeeExtraFields) return;

        const quantityInputId = this.attendeeFields.dataset.quantityInputId;
        if (!quantityInputId) return;

        this.attendeeQuantityInput = document.getElementById(quantityInputId);
        if (!this.attendeeQuantityInput) return;

        this.renderAttendeeFields = this.renderAttendeeFields.bind(this);
        this.attendeeQuantityInput.addEventListener('change', this.renderAttendeeFields);
        this.attendeeQuantityInput.addEventListener('input', this.renderAttendeeFields);
        this.renderAttendeeFields();
      }

      renderAttendeeFields() {
        if (!this.attendeeExtraFields || !this.attendeeQuantityInput) return;

        const quantityValue = parseInt(this.attendeeQuantityInput.value, 10);
        const quantity = Number.isNaN(quantityValue) ? 1 : Math.max(quantityValue, 1);
        const additionalAttendees = Math.max(quantity - 1, 0);
        const existingValues = {};

        this.attendeeExtraFields.querySelectorAll('input').forEach((input) => {
          existingValues[input.name] = input.value;
        });

        if (additionalAttendees === 0) {
          this.attendeeExtraFields.innerHTML = '';
          this.attendeeExtraFields.setAttribute('hidden', 'hidden');
          return;
        }

        const sectionId = this.attendeeFields.dataset.sectionId || 'section';
        let fieldsMarkup = '';

        for (let attendeeNumber = 2; attendeeNumber <= quantity; attendeeNumber++) {
          const attendeeNameProperty = `properties[Attendee ${attendeeNumber} Name]`;
          const attendeeEmailProperty = `properties[Attendee ${attendeeNumber} Email]`;
          const attendeeNameValue = existingValues[attendeeNameProperty] || '';
          const attendeeEmailValue = existingValues[attendeeEmailProperty] || '';

          fieldsMarkup += `
            <div class="attendee-fields__row">
              <div class="field">
                <input
                  class="field__input"
                  type="text"
                  id="Attendee-${sectionId}-${attendeeNumber}-Name"
                  name="${attendeeNameProperty}"
                  value="${this.escapeHtml(attendeeNameValue)}"
                  autocomplete="name"
                  required
                >
                <label class="field__label" for="Attendee-${sectionId}-${attendeeNumber}-Name">
                  Attendee ${attendeeNumber} name
                </label>
              </div>
              <div class="field">
                <input
                  class="field__input"
                  type="email"
                  id="Attendee-${sectionId}-${attendeeNumber}-Email"
                  name="${attendeeEmailProperty}"
                  value="${this.escapeHtml(attendeeEmailValue)}"
                  autocomplete="email"
                  inputmode="email"
                  required
                >
                <label class="field__label" for="Attendee-${sectionId}-${attendeeNumber}-Email">
                  Attendee ${attendeeNumber} email
                </label>
              </div>
            </div>
          `;
        }

        this.attendeeExtraFields.innerHTML = fieldsMarkup;
        this.attendeeExtraFields.removeAttribute('hidden');
      }

      escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (char) => {
          const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          };
          return map[char];
        });
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
